import * as React from 'react'
import { DocumentPreviewItem, FormElement, FORM_ELEMENT_TYPES } from 'prace-common-components'
import { useGetElementDocumentsQuery, useLazyGetDocumentVersionUrlQuery } from 'store/api/calls/documents'

type PreviewFormElementProps = {
  element: any,
  whiteBackground?: boolean,
}

/**
 * Renders a flow element in the call editor's live preview. For `document_preview` elements it resolves
 * the admin-uploaded documents (which are stored separately from the element) and wires `onViewDocument`
 * to open the latest version via a presigned URL — without this, the preview always shows "No documents
 * available." because `element.documents` is never populated on the call flow.
 */
export const PreviewFormElement: React.FC<PreviewFormElementProps> = ({ element, whiteBackground }) => {
  const isDocPreview = element.elementType === FORM_ELEMENT_TYPES.DOCUMENT_PREVIEW
  const { data: documents } = useGetElementDocumentsQuery(element.id, { skip: !isDocPreview })
  const [getVersionUrl] = useLazyGetDocumentVersionUrlQuery()

  /* Memoized so the reference is stable across renders — FormElement's memo compares `documents` by
   * reference, so a fresh array every render would defeat it (or re-render needlessly). Computed
   * before the early return below to keep hook order stable (rules-of-hooks). */
  const previewDocuments: DocumentPreviewItem[] = React.useMemo(() => (documents || []).reduce((acc, doc) => {
    const current = doc.versions?.length ? doc.versions[doc.versions.length - 1] : undefined
    /* The preview can only open documents that have at least one uploaded version */
    if (current) acc.push({ id: current.id, versionId: current.id, filename: current.originalFilename || 'Document' })
    return acc
  }, [] as DocumentPreviewItem[]), [documents])

  if (!isDocPreview) return <FormElement whiteBackground={whiteBackground} element={element} />

  const handleView = async (versionId: StringNumber) => {
    /* Open the tab synchronously to avoid the popup blocker, then point it at the presigned URL */
    const tab = window.open('', '_blank')
    try {
      const { url } = await getVersionUrl(Number(versionId)).unwrap()
      if (tab) tab.location.href = url
    } catch {
      if (tab) tab.close()
    }
  }

  return (
    <FormElement
      whiteBackground={whiteBackground}
      element={{ ...element, documents: previewDocuments, onViewDocument: handleView }}
    />
  )
}
